import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Plus, Edit2, Trash2, Home, MapPin, DollarSign, X, Search, Upload } from 'lucide-react';
import { assetService } from '../services/assetService';
import { cloudinaryService } from '../services/cloudinaryService';
import SequenceManager from '../components/SequenceManager';

const AssetsPage = () => {
  const navigate = useNavigate();
  const exteriorFileInputRef = useRef(null);
  const interiorFileInputRef = useRef(null);
  const brochureFileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSequenceDialogOpen, setIsSequenceDialogOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [featureInput, setFeatureInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [bodyTypeInput, setBodyTypeInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [imageCategory, setImageCategory] = useState('exterior');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [brochureToDelete, setBrochureToDelete] = useState(null);
  const [pendingBrochureFile, setPendingBrochureFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    asset_identification_number: '', // Asset ID field
    category: '',
    description: '',
    cities: [], // Changed from city to cities (array)
    body_types: [], // New field for car type filter
    price_per_ticket: 0,
    images: [],
    exterior_images: [],
    interior_images: [],
    exterior_count: 0, // Track split for proper display
    total_tickets: 0,
    features: [],
    status: 'active',
    brochure_url: '' // New field for brochure PDF
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await assetService.getAllAssets();
      setAssets(data);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError(err.response?.data?.detail || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (asset = null) => {
    if (asset) {
      setEditingAsset(asset);
      
      // Check if backend already has separate exterior/interior fields
      let exteriorImages = [];
      let interiorImages = [];
      
      if (asset.exterior_images && asset.interior_images) {
        // New format: backend provides separate arrays
        exteriorImages = asset.exterior_images || [];
        interiorImages = asset.interior_images || [];
      } else {
        // Old format: split images array for backward compatibility
        const allImages = asset.images || [];
        const midPoint = Math.ceil(allImages.length / 2);
        exteriorImages = allImages.slice(0, midPoint);
        interiorImages = allImages.slice(midPoint);
      }
      
      // Handle backward compatibility for cities and body_types
      let cities = asset.cities || [];
      if (!cities.length && asset.city) {
        // Convert old single city field to array
        cities = [asset.city];
      }
      
      console.log('📄 Loading asset for editing:', {
        name: asset.name,
        brochure_url: asset.brochure_url,
        hasBrochure: !!asset.brochure_url
      });

      setFormData({
        name: asset.name || '',
        asset_identification_number: asset.asset_identification_number || '',
        category: asset.category || '',
        description: asset.description || '',
        cities: cities,
        body_types: asset.body_types || [],
        price_per_ticket: asset.price_per_ticket || 0,
        images: asset.images || [],
        exterior_images: exteriorImages,
        interior_images: interiorImages,
        exterior_count: exteriorImages.length,
        total_tickets: asset.total_tickets || 0,
        features: asset.features || [],
        status: asset.status || 'active',
        brochure_url: asset.brochure_url || ''
      });
    } else {
      setEditingAsset(null);
      setFormData({
        name: '',
        asset_identification_number: '',
        category: '',
        description: '',
        cities: [],
        body_types: [],
        price_per_ticket: 0,
        images: [],
        exterior_images: [],
        interior_images: [],
        exterior_count: 0,
        total_tickets: 0,
        features: [],
        status: 'active',
        brochure_url: ''
      });
    }
    setFeatureInput('');
    setCityInput('');
    setBodyTypeInput('');
    setImageCategory('exterior');
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAsset(null);
    setFeatureInput('');
    setImagesToDelete([]); // Clear deletion queue when closing dialog
    setBrochureToDelete(null); // Clear brochure deletion
    setPendingBrochureFile(null); // Clear pending brochure file
  };

  const handleImageUploadClick = (category) => {
    if (category === 'exterior') {
      exteriorFileInputRef.current?.click();
    } else {
      interiorFileInputRef.current?.click();
    }
  };

  const handleImageFileChange = async (e, category) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);
    setError('');
    
    try {
      // Cloudinary configuration
      const CLOUDINARY_CLOUD_NAME = 'dkallcbko';
      const CLOUDINARY_UPLOAD_PRESET = 'luxury_assets_unsigned';
      const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
      
      // Map asset category to folder name
      const folderMapping = {
        'vehicle': 'The Haute Circle/Vehicles',
        'jet': 'The Haute Circle/Private Jets',
        'yacht': 'The Haute Circle/Yachts',
        'property': 'The Haute Circle/Vacation Homes'
      };
      
      const baseFolder = folderMapping[formData.category] || 'The Haute Circle/Other';
      const fullFolder = `${baseFolder}/${category}`;
      
      // Upload all files to Cloudinary
      const uploadPromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
          formData.append('folder', fullFolder);
          formData.append('tags', `${category},luxury_asset,${formData.category}`);
          
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              console.log(`Uploading ${file.name}: ${percentComplete}%`);
            }
          });
          
          xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
              const response = JSON.parse(xhr.responseText);
              resolve(response.secure_url);
            } else {
              reject(new Error(`Upload failed: ${xhr.statusText}`));
            }
          });
          
          xhr.addEventListener('error', () => {
            reject(new Error(`Network error uploading ${file.name}`));
          });
          
          xhr.open('POST', CLOUDINARY_UPLOAD_URL);
          xhr.send(formData);
        });
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      
      if (category === 'exterior') {
        const newExteriorImages = [...formData.exterior_images, ...uploadedUrls];
        setFormData({
          ...formData,
          exterior_images: newExteriorImages,
          images: [...newExteriorImages, ...formData.interior_images]
        });
      } else {
        const newInteriorImages = [...formData.interior_images, ...uploadedUrls];
        setFormData({
          ...formData,
          interior_images: newInteriorImages,
          images: [...formData.exterior_images, ...newInteriorImages]
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      setError(`Failed to upload images: ${error.message}. Please try again.`);
    } finally {
      setUploadingImage(false);
      // Reset file input
      const inputRef = category === 'exterior' ? exteriorFileInputRef : interiorFileInputRef;
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = (index, category) => {
    const imageToDelete = category === 'exterior' 
      ? formData.exterior_images[index] 
      : formData.interior_images[index];
    
    // Mark Cloudinary images for deletion (will be deleted on save)
    if (imageToDelete && imageToDelete.includes('cloudinary.com')) {
      setImagesToDelete(prev => [...prev, imageToDelete]);
      console.log('🗑️ Image marked for deletion (will delete on save):', imageToDelete);
    }
    
    // Remove from local state immediately for UI feedback
    if (category === 'exterior') {
      const newExteriorImages = formData.exterior_images.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        exterior_images: newExteriorImages,
        images: [...newExteriorImages, ...formData.interior_images],
        exterior_count: newExteriorImages.length
      });
    } else {
      const newInteriorImages = formData.interior_images.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        interior_images: newInteriorImages,
        images: [...formData.exterior_images, ...newInteriorImages]
      });
    }
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const handleAddCity = () => {
    if (cityInput.trim() && !formData.cities.includes(cityInput.trim())) {
      setFormData({
        ...formData,
        cities: [...formData.cities, cityInput.trim()]
      });
      setCityInput('');
    }
  };

  const handleRemoveCity = (index) => {
    setFormData({
      ...formData,
      cities: formData.cities.filter((_, i) => i !== index)
    });
  };

  const handleAddBodyType = () => {
    if (bodyTypeInput.trim() && !formData.body_types.includes(bodyTypeInput.trim())) {
      setFormData({
        ...formData,
        body_types: [...formData.body_types, bodyTypeInput.trim()]
      });
      setBodyTypeInput('');
    }
  };

  const handleRemoveBodyType = (index) => {
    setFormData({
      ...formData,
      body_types: formData.body_types.filter((_, i) => i !== index)
    });
  };

  const handleBrochureUploadClick = () => {
    brochureFileInputRef.current?.click();
  };

  const handleBrochureFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('pdf')) {
      alert('❌ Invalid File Type\n\nOnly PDF files are allowed for brochures.\n\nPlease select a PDF file.');
      if (brochureFileInputRef.current) {
        brochureFileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (10MB limit)
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (file.size > 10 * 1024 * 1024) {
      alert(`❌ File Size Too Large\n\nMaximum allowed size: 10 MB\nYour file size: ${fileSizeMB} MB\n\nPlease compress the PDF or choose a smaller file.`);
      if (brochureFileInputRef.current) {
        brochureFileInputRef.current.value = '';
      }
      return;
    }

    // Show file size info
    console.log(`📄 File selected: ${file.name} (${fileSizeMB} MB) - Will upload on save`);
    
    // Confirm if replacing existing brochure
    if (formData.brochure_url) {
      const confirmReplace = window.confirm(
        `⚠️ Replace Existing Brochure?\n\n` +
        `You already have a brochure uploaded.\n` +
        `Do you want to replace it with:\n\n` +
        `${file.name} (${fileSizeMB} MB)\n\n` +
        `The old brochure will be deleted when you save.`
      );
      if (!confirmReplace) {
        if (brochureFileInputRef.current) {
          brochureFileInputRef.current.value = '';
        }
        return;
      }
    }

    // Store file for upload on save, don't upload immediately
    setPendingBrochureFile(file);
    
    // Mark old brochure for deletion if exists
    if (formData.brochure_url && formData.brochure_url.includes('cloudinary.com')) {
      setBrochureToDelete(formData.brochure_url);
    }

    // Update UI to show pending upload
    setFormData({
      ...formData,
      brochure_url: `pending:${file.name}` // Temporary indicator
    });

    console.log(`✅ Brochure file ready for upload: ${file.name} (${fileSizeMB} MB)`);
  };

  const handleRemoveBrochure = () => {
    if (formData.brochure_url && formData.brochure_url.includes('cloudinary.com')) {
      setBrochureToDelete(formData.brochure_url);
    }
    setPendingBrochureFile(null); // Clear any pending file
    setFormData({
      ...formData,
      brochure_url: ''
    });
    
    // Clear file input
    if (brochureFileInputRef.current) {
      brochureFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      // Delete marked images from Cloudinary before saving
      if (imagesToDelete.length > 0) {
        console.log(`🗑️ Deleting ${imagesToDelete.length} images from Cloudinary...`);
        
        for (const imageUrl of imagesToDelete) {
          try {
            const result = await cloudinaryService.deleteImage(imageUrl);
            if (result.success) {
              console.log('✅ Deleted from Cloudinary:', imageUrl.substring(imageUrl.lastIndexOf('/') + 1));
            } else {
              console.warn('⚠️ Could not delete from Cloudinary:', result.message);
            }
          } catch (delError) {
            console.error('❌ Error deleting image:', delError);
            // Continue with other deletions even if one fails
          }
        }
      }

      // Delete old brochure if marked for deletion
      if (brochureToDelete) {
        console.log('🗑️ Deleting old brochure from Cloudinary...');
        try {
          const result = await cloudinaryService.deleteImage(brochureToDelete);
          if (result.success) {
            console.log('✅ Deleted brochure from Cloudinary');
          } else {
            console.warn('⚠️ Could not delete brochure:', result.message);
          }
        } catch (delError) {
          console.error('❌ Error deleting brochure:', delError);
        }
      }

      // Upload pending brochure file if exists
      let finalBrochureUrl = formData.brochure_url;
      if (pendingBrochureFile) {
        console.log('📤 Uploading pending brochure file to Cloudinary...');
        setUploadingBrochure(true);
        try {
          const result = await cloudinaryService.uploadPdf(pendingBrochureFile, formData.category);
          finalBrochureUrl = result.secure_url;
          console.log('✅ Brochure uploaded successfully:', result.secure_url);
          setPendingBrochureFile(null); // Clear pending file
        } catch (uploadError) {
          console.error('❌ Error uploading brochure:', uploadError);
          setError(`Failed to upload brochure: ${uploadError.message}`);
          setUploadingBrochure(false);
          return; // Stop submission if brochure upload fails
        } finally {
          setUploadingBrochure(false);
        }
      }
      
      // Prepare data with separate exterior and interior images
      const dataToSubmit = {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        cities: formData.cities, // Array of cities
        body_types: formData.body_types, // Array of body types
        price_per_ticket: formData.price_per_ticket,
        total_tickets: formData.total_tickets,
        features: formData.features,
        status: formData.status,
        brochure_url: finalBrochureUrl, // Use the final brochure URL (uploaded or existing)
        // Send separate image arrays for proper backend storage
        exterior_images: formData.exterior_images,
        interior_images: formData.interior_images,
        // Backend should auto-populate images from exterior + interior
        images: [...formData.exterior_images, ...formData.interior_images]
      };
      
      if (editingAsset) {
        await assetService.updateAsset(editingAsset.id, dataToSubmit);
      } else {
        await assetService.createAsset(dataToSubmit);
      }
      
      await fetchAssets();
      setImagesToDelete([]); // Clear deletion queue after successful save
      setBrochureToDelete(null); // Clear brochure deletion
      setPendingBrochureFile(null); // Clear pending brochure file
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving asset:', err);
      setError(err.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleDeleteClick = (asset) => {
    setAssetToDelete(asset);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assetToDelete) return;
    
    setError('');
    try {
      // Delete all asset images from Cloudinary using consolidated method
      try {
        console.log('🗑️ Deleting all Cloudinary images for asset:', assetToDelete.name);
        await assetService.deleteAssetImages(assetToDelete);
        console.log('✅ Cloudinary cleanup completed');
      } catch (err) {
        console.error('❌ Failed to clean up Cloudinary images:', err);
        // Continue with asset deletion even if image cleanup fails
      }

      // Delete asset from database
      await assetService.deleteAsset(assetToDelete.id);
      await fetchAssets();
      setDeleteConfirmOpen(false);
      setAssetToDelete(null);
    } catch (err) {
      console.error('Error deleting asset:', err);
      setError(err.response?.data?.detail || 'Failed to delete asset');
      setDeleteConfirmOpen(false);
      setAssetToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAssetToDelete(null);
  };

  // ========== SEQUENCE MANAGEMENT ==========
  
  const handleUpdateSequences = async (sequences) => {
    setError('');
    try {
      await assetService.updateAssetSequences(sequences);
      setError(''); // Clear any existing errors
      // Show success message (you might want to add a success state)
      console.log('Asset display sequence updated successfully!');
      await fetchAssets();
    } catch (err) {
      console.error('Error updating sequences:', err);
      setError(err.response?.data?.detail || 'Failed to update asset sequences');
      throw err; // Re-throw to handle in SequenceManager
    }
  };

  const filteredAssets = assets.filter(asset => {
    const searchLower = searchTerm.toLowerCase();
    return (
      asset.name?.toLowerCase().includes(searchLower) ||
      asset.category?.toLowerCase().includes(searchLower) ||
      asset.city?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asset Management</h1>
              <p className="text-sm text-gray-600 mt-1">Manage product listings and inventory</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setIsSequenceDialogOpen(true)}
              className="flex items-center gap-2"
              disabled={assets.length === 0}
            >
              📋 Manage Sequence
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-black hover:bg-gray-800 text-white flex items-center gap-2"
            >
              <Plus size={16} />
              Add Asset
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder="Search by name, category, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Assets Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading assets...</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {searchTerm ? 'No assets found matching your search.' : 'No assets created yet. Click "Add Asset" to create one.'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssets.map((asset) => (
              <Card key={asset.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  {asset.images && asset.images.length > 0 && (
                    <div className="w-full h-48 mb-4 rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={asset.images[0]}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-bold">{asset.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="px-3 py-1 text-xs font-medium bg-black text-white rounded-full uppercase">
                          {asset.category}
                        </span>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          asset.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {asset.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4 line-clamp-2 text-gray-600">
                    {asset.description}
                  </CardDescription>
                  
                  <div className="space-y-2 mb-4 text-sm">
                    {/* Display cities */}
                    {(asset.cities && asset.cities.length > 0) ? (
                      <div className="flex items-start gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <div className="flex flex-wrap gap-1">
                          {asset.cities.map((city, idx) => (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                              {city}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : asset.city && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4" />
                        <span>{asset.city}</span>
                      </div>
                    )}
                    
                    {/* Display body types */}
                    {asset.body_types && asset.body_types.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {asset.body_types.map((type, idx) => (
                          <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-medium">
                            {type}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-gray-900 font-bold">
                      <DollarSign className="w-4 h-4" />
                      <span>₹{asset.price_per_ticket?.toLocaleString()}/ticket</span>
                    </div>
                    {asset.total_tickets > 0 && (
                      <div className="flex items-center justify-between text-xs mt-2">
                        <span className="text-gray-600">Tickets Sold:</span>
                        <span className="font-semibold">{asset.sold_tickets || 0}/{asset.total_tickets}</span>
                      </div>
                    )}
                  </div>

                  {asset.features && asset.features.length > 0 && (
                    <div className="mb-4 pb-4 border-b border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2">Key Features:</p>
                      <div className="flex flex-wrap gap-1">
                        {asset.features.slice(0, 2).map((feature, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                        {asset.features.length > 2 && (
                          <span className="text-xs text-gray-500">+{asset.features.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(asset)}
                      className="flex-1 border-gray-300 hover:bg-gray-50"
                    >
                      <Edit2 size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(asset)}
                      className="text-red-600 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Asset Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Lamborghini Aventador"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="asset_identification_number">Asset ID</Label>
                <Input
                  id="asset_identification_number"
                  value={formData.asset_identification_number || ''}
                  onChange={(e) => setFormData({ ...formData, asset_identification_number: e.target.value })}
                  placeholder="e.g., THC-VEH-001"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="jet">Private Jet</option>
                  <option value="yacht">Yacht</option>
                  <option value="property">Property</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter detailed asset description"
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>

            <div className="space-y-2">
              <Label>Available Cities *</Label>
              <div className="flex gap-2">
                <Input
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  placeholder="e.g., Mumbai, Delhi, Bangalore"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCity();
                    }
                  }}
                />
                <Button type="button" onClick={handleAddCity} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              {formData.cities.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.cities.map((city, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                    >
                      {city}
                      <button
                        type="button"
                        onClick={() => handleRemoveCity(index)}
                        className="hover:bg-blue-100 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">Add cities where this asset is available for use</p>
            </div>

            <div className="space-y-2">
              <Label>Body Type (Car Type) *</Label>
              <div className="flex gap-2">
                <Input
                  value={bodyTypeInput}
                  onChange={(e) => setBodyTypeInput(e.target.value)}
                  placeholder="e.g., Supercar, SUV, Sedan, Coupe"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddBodyType();
                    }
                  }}
                />
                <Button type="button" onClick={handleAddBodyType} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              {formData.body_types.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.body_types.map((type, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={() => handleRemoveBodyType(index)}
                        className="hover:bg-purple-100 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500">Add body types for Car Type filter (e.g., Supercar, SUV)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="total_tickets">Total Tickets *</Label>
                <Input
                  id="total_tickets"
                  type="number"
                  value={formData.total_tickets}
                  onChange={(e) => setFormData({ ...formData, total_tickets: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 12"
                  required
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price_per_ticket">Price per Ticket (₹) *</Label>
                <Input
                  id="price_per_ticket"
                  type="number"
                  step="0.01"
                  value={formData.price_per_ticket}
                  onChange={(e) => setFormData({ ...formData, price_per_ticket: parseFloat(e.target.value) || 0 })}
                  placeholder="e.g., 2500000"
                  required
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="space-y-3">
              <Label>Asset Images</Label>
              
              {/* Image Category Tabs */}
              <div className="flex gap-2 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setImageCategory('exterior')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    imageCategory === 'exterior'
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  EXTERIOR
                </button>
                <button
                  type="button"
                  onClick={() => setImageCategory('interior')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    imageCategory === 'interior'
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  INTERIOR
                </button>
              </div>

              {/* Hidden file inputs */}
              <input
                ref={exteriorFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageFileChange(e, 'exterior')}
                className="hidden"
              />
              <input
                ref={interiorFileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageFileChange(e, 'interior')}
                className="hidden"
              />

              {/* Exterior Images Section */}
              {imageCategory === 'exterior' && (
                <div className="space-y-3">
                  <Button
                    type="button"
                    onClick={() => handleImageUploadClick('exterior')}
                    variant="outline"
                    className="w-full border-2 border-dashed border-gray-300 hover:border-gray-400 h-24"
                    disabled={uploadingImage}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={24} className="text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploadingImage ? 'Uploading...' : 'Upload Exterior Images'}
                      </span>
                      <span className="text-xs text-gray-400">
                        JPG, PNG, WEBP
                      </span>
                    </div>
                  </Button>
                  
                  {formData.exterior_images.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        {formData.exterior_images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={img}
                              alt={`Exterior ${index + 1}`}
                              className="w-full h-24 object-cover rounded border border-gray-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/150?text=Error';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index, 'exterior')}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={14} />
                            </button>
                            <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                              {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formData.exterior_images.length} exterior image{formData.exterior_images.length !== 1 ? 's' : ''}
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Interior Images Section */}
              {imageCategory === 'interior' && (
                <div className="space-y-3">
                  <Button
                    type="button"
                    onClick={() => handleImageUploadClick('interior')}
                    variant="outline"
                    className="w-full border-2 border-dashed border-gray-300 hover:border-gray-400 h-24"
                    disabled={uploadingImage}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={24} className="text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploadingImage ? 'Uploading...' : 'Upload Interior Images'}
                      </span>
                      <span className="text-xs text-gray-400">
                        JPG, PNG, WEBP
                      </span>
                    </div>
                  </Button>
                  
                  {formData.interior_images.length > 0 && (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        {formData.interior_images.map((img, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={img}
                              alt={`Interior ${index + 1}`}
                              className="w-full h-24 object-cover rounded border border-gray-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://via.placeholder.com/150?text=Error';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index, 'interior')}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X size={14} />
                            </button>
                            <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                              {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formData.interior_images.length} interior image{formData.interior_images.length !== 1 ? 's' : ''}
                      </p>
                    </>
                  )}
                </div>
              )}
              
              {/* Total images count */}
              {(formData.exterior_images.length > 0 || formData.interior_images.length > 0) && (
                <div className="flex gap-4 text-xs text-gray-600 pt-2 border-t border-gray-200">
                  <span>Exterior: {formData.exterior_images.length}</span>
                  <span>Interior: {formData.interior_images.length}</span>
                  <span className="font-semibold">Total: {formData.exterior_images.length + formData.interior_images.length}</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Key Features</Label>
              <div className="flex gap-2">
                <Input
                  value={featureInput}
                  onChange={(e) => setFeatureInput(e.target.value)}
                  placeholder="e.g., 770 HP V12 Engine"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                />
                <Button type="button" onClick={handleAddFeature} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              {formData.features.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.features.map((feature, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(index)}
                        className="hover:bg-gray-200 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Asset Brochure (PDF)</Label>
              <input
                ref={brochureFileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleBrochureFileChange}
                className="hidden"
              />
              
              {/* Debug info */}
              {console.log('🔍 Brochure Display Check:', {
                brochure_url: formData.brochure_url,
                isEmpty: !formData.brochure_url,
                type: typeof formData.brochure_url
              })}
              
              {!formData.brochure_url ? (
                <Button
                  type="button"
                  onClick={handleBrochureUploadClick}
                  variant="outline"
                  className="w-full border-2 border-dashed border-gray-300 hover:border-gray-400 h-20"
                  disabled={uploadingBrochure}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Upload size={20} className="text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {uploadingBrochure ? 'Uploading brochure...' : 'Click to upload brochure (PDF only)'}
                    </span>
                    <span className="text-xs text-gray-400">
                      Max size: 10MB
                    </span>
                  </div>
                </Button>
              ) : (
                <div className={`flex items-center gap-3 p-4 border rounded ${
                  formData.brochure_url.startsWith('pending:') 
                    ? 'bg-yellow-50 border-yellow-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                      </svg>
                      <div>
                        <p className={`text-sm font-medium ${
                          formData.brochure_url.startsWith('pending:') 
                            ? 'text-yellow-800' 
                            : 'text-green-800'
                        }`}>
                          {formData.brochure_url.startsWith('pending:') 
                            ? 'Brochure ready to upload' 
                            : 'Brochure uploaded'}
                        </p>
                        <p className={`text-xs truncate max-w-xs ${
                          formData.brochure_url.startsWith('pending:') 
                            ? 'text-yellow-600' 
                            : 'text-green-600'
                        }`}>
                          {formData.brochure_url.startsWith('pending:') 
                            ? formData.brochure_url.replace('pending:', '') + ' (will upload on save)'
                            : formData.brochure_url.split('/').pop()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!formData.brochure_url.startsWith('pending:') && (
                      <Button
                        type="button"
                        onClick={() => window.open(formData.brochure_url, '_blank')}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        View
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={handleRemoveBrochure}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-2 text-xs text-gray-500">
                <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                </svg>
                <span>Upload PDF brochure for users to download. Only 1 PDF allowed per asset. Max size: 10 MB.</span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white">
                {editingAsset ? 'Update Asset' : 'Create Asset'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              Delete Asset
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete this asset?
            </p>
            {assetToDelete && (
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="font-semibold text-gray-900">{assetToDelete.name}</p>
                <p className="text-sm text-gray-600 mt-1">{assetToDelete.category}</p>
              </div>
            )}
            <p className="text-sm text-red-600 mt-4 font-medium">
              ⚠️ This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleDeleteCancel}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sequence Manager */}
      <SequenceManager
        items={assets}
        isOpen={isSequenceDialogOpen}
        onClose={() => setIsSequenceDialogOpen(false)}
        onSave={handleUpdateSequences}
        itemDisplayKey="name"
        title="Manage Asset Display Sequence"
      />
    </div>
  );
};

export default AssetsPage;
